import { HeroIcons } from '../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import useLocale from '../../hooks/useLocale'
import { getLocaleAppBaseUrl } from '../../lib/utils/language'
import Link from '../shared/Link'
import routes from '../../config/route'
import BackgroundPicture from '../shared/BackgroundPicture'
import gtmService, { DestinationType } from '../../services/gtm.service'

export default function HeroSection() {
  const { t } = useTranslation('index')
  const { locale } = useLocale()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const actions = [
    {
      label: t('landing.hero.actions.primary'),
      href: getLocaleAppBaseUrl(locale, 'register'),
      external: true,
      destinationType: 'app_register' as DestinationType,
      className:
        'inline-flex h-12 w-full items-center justify-center rounded-button bg-gradient px-5 font-medium text-shelters-hero-action text-white shadow-[0_8px_28px_rgba(0,0,0,0.22)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:scale-[1.02] hover:brightness-110 hover:shadow-[0_12px_34px_rgba(51,40,154,0.42)] tablet:h-14 tablet:px-7',
    },
    {
      label: t('landing.hero.actions.secondary'),
      href: routes.contact,
      external: false,
      destinationType: 'contact' as DestinationType,
      icon: HeroIcons.ChatBubbleLeftRightIcon,
      className:
        'inline-flex h-12 w-full items-center justify-center rounded-button border border-white/45 bg-white/12 px-5 font-medium text-shelters-hero-action text-white shadow-[0_8px_28px_rgba(0,0,0,0.18)] backdrop-blur-lg transition-all duration-300 ease-out hover:-translate-y-0.5 hover:scale-[1.02] hover:border-white/65 hover:bg-white/20 hover:shadow-[0_12px_34px_rgba(0,0,0,0.3)] tablet:h-14 tablet:px-7',
    },
  ]

  const revealClass = `transform-gpu transition-all duration-700 ease-out ${
    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
  }`

  return (
    <section
      data-navtheme="frosted"
      className="relative min-h-[100dvh] w-full overflow-hidden"
    >
      <BackgroundPicture
        fallbackSrc="/img/backgrounds/bg-hero-mockup-mobile.png"
        objectPositionClassName="object-[center_top] tablet:object-center"
        sources={[
          {
            media: '(min-width: 768px)',
            srcSet: '/img/backgrounds/bg-hero-mockup-desktop.webp',
            type: 'image/webp',
          },
          {
            media: '(min-width: 768px)',
            srcSet: '/img/backgrounds/bg-hero-mockup-desktop.png',
            type: 'image/png',
          },
          {
            srcSet: '/img/backgrounds/bg-hero-mockup-mobile.webp',
            type: 'image/webp',
          },
        ]}
      />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.58)_0%,rgba(0,0,0,0.38)_30%,rgba(0,0,0,0.16)_56%,rgba(0,0,0,0.74)_100%)] tablet:hidden" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[52dvh] bg-[linear-gradient(180deg,rgba(0,0,0,0.44)_0%,rgba(0,0,0,0.28)_58%,rgba(0,0,0,0)_100%)] tablet:hidden" />

      <div className="relative z-10 flex min-h-[100dvh] w-full px-3 pb-8 pt-28 text-white tablet:items-center tablet:justify-end tablet:px-16 tablet:pb-16 tablet:pt-32 desktop:px-24 2xl:px-44">
        <div className="flex min-h-[calc(100dvh-9rem)] w-full flex-col justify-between tablet:ml-auto tablet:min-h-0 tablet:max-w-[880px] tablet:justify-start desktop:max-w-[920px]">
          <h1
            className={`${revealClass} mx-auto max-w-[335px] text-center font-bold text-white drop-shadow-[0_6px_22px_rgba(0,0,0,0.88)] text-shelters-hero-display tablet:mx-0 tablet:max-w-[720px] tablet:text-left tablet:text-[#f6f2e9] tablet:drop-shadow-none desktop:max-w-[920px]`}
            style={{ transitionDelay: '60ms' }}
          >
            <span className="block">{t('landing.hero.title.line1')}</span>{' '}
            <span className="block">{t('landing.hero.title.line2')}</span>
          </h1>

          <div className="pb-1 tablet:pb-0">
            <p
              className={`${revealClass} tablet:text-[#f2ece0]/92 mx-auto max-w-[330px] text-center font-medium text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.68)] text-shelters-hero-lead tablet:mx-0 tablet:mt-7 tablet:max-w-2xl tablet:text-left tablet:font-normal tablet:drop-shadow-none`}
              style={{ transitionDelay: '180ms' }}
            >
              {t('landing.hero.lead')}
            </p>

            <div className="mx-auto mt-5 grid w-full grid-cols-1 gap-3 sm:grid-cols-2 tablet:mx-0 tablet:mt-8 tablet:max-w-xl tablet:gap-4">
              {actions.map((action, index) => {
                const content = (
                  <>
                    {action.icon ? (
                      <action.icon className="mr-2 inline h-5 w-5" />
                    ) : null}
                    {action.label}
                  </>
                )
                return (
                  <div
                    key={`${action.className}-${index}`}
                    className={revealClass}
                    style={{ transitionDelay: `${300 + index * 100}ms` }}
                  >
                    {action.external ? (
                      <a
                        href={action.href}
                        onClick={() =>
                          gtmService.ctaClicked({
                            ctaId: `hero_${action.destinationType}`,
                            ctaLabel: action.label,
                            ctaLocation: 'hero',
                            destinationUrl: action.href,
                            destinationType: action.destinationType,
                            language: locale,
                          })
                        }
                        className={action.className}
                      >
                        {content}
                      </a>
                    ) : (
                      <Link
                        href={action.href}
                        onClick={() =>
                          gtmService.ctaClicked({
                            ctaId: `hero_${action.destinationType}`,
                            ctaLabel: action.label,
                            ctaLocation: 'hero',
                            destinationUrl: action.href,
                            destinationType: action.destinationType,
                            language: locale,
                          })
                        }
                        className={action.className}
                      >
                        {content}
                      </Link>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
